import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Nav from '@src/routes';
import { get } from 'lodash-es';

const REDIRECT_URL_KEY = 'art#cb#redirectUrl';

// Only accept internal, absolute paths. Rejects external/protocol-relative URLs (`//evil.com`,
// `https://evil.com`) to prevent open-redirect via a tampered sessionStorage value. Also rejects
// root `/` — that's the SSO redirect hub (RootRedirect), not a destination; honoring it would send
// RootRedirect back to `/` and stick on a blank root instead of falling back to Submissions.
const isSafeInternalPath = (url: string | null): url is string =>
  !!url && url.startsWith('/') && !url.startsWith('//') && url !== Nav.Home;

export const useRememberRedirectUrl = () => {
  const location = useLocation();

  const getRedirectUrlOnce = useCallback((defaultUrl: string) => {
    const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY);
    sessionStorage.removeItem(REDIRECT_URL_KEY);
    return isSafeInternalPath(redirectUrl) ? redirectUrl : defaultUrl;
  }, []);

  const captureRedirectUrl = useCallback(() => {
    const pathname = get(location, 'state.from.pathname');

    if (pathname) {
      sessionStorage.setItem(REDIRECT_URL_KEY, pathname);
    }
  }, [location]);

  return { getRedirectUrlOnce, captureRedirectUrl };
};
